<template>
  <!-- Users assigned to a single customer never see this; the header behaves
       exactly as it did before for them. -->
  <div v-if="authStore.canSwitchCustomer" class="customer-switcher">
    <Button
      severity="secondary"
      text
      class="max-w-56 md:max-w-72"
      data-cy="customer-switcher"
      aria-haspopup="true"
      :aria-expanded="menuOpen"
      :aria-label="t('nav.customerSwitch.trigger', { name: activeName })"
      :loading="switching"
      @click="toggleMenu"
    >
      <i class="pi pi-building shrink-0" />
      <span class="truncate font-semibold">{{ activeName }}</span>
      <span v-if="activeNumber" class="shrink-0 text-grey-500">· {{ activeNumber }}</span>
      <i class="pi pi-chevron-down shrink-0 text-xs" />
    </Button>

    <Menu
      ref="menu"
      :model="customerItems"
      :popup="true"
      class="w-80"
      data-cy="customer-switcher-menu"
      @show="menuOpen = true"
      @hide="menuOpen = false"
    >
      <template #start>
        <p class="px-3 pb-2 pt-3 text-xs font-semibold uppercase tracking-wide text-grey-400">
          {{ t('nav.customerSwitch.title') }}
        </p>
      </template>

      <template #item="{ item, props }">
        <a class="flex items-center gap-3" v-bind="props.action">
          <i
            class="pi pi-check w-4 shrink-0 text-primary-500"
            :class="item.isActive ? 'opacity-100' : 'opacity-0'"
          />
          <span class="flex min-w-0 flex-col">
            <!-- Several companies of a group can share a name, so the customer
                 number is always shown alongside it. -->
            <span class="truncate" :class="item.isActive ? 'font-semibold' : ''">
              {{ item.label }}
            </span>
            <span class="text-xs text-grey-500">
              {{ t('nav.customerSwitch.customerNo', { number: item.number }) }}
              <template v-if="item.isActive"> · {{ t('nav.customerSwitch.current') }}</template>
            </span>
          </span>
        </a>
      </template>

      <template #end>
        <p class="mt-1 border-t border-grey-200 px-3 py-2 text-xs text-grey-500">
          {{ t('nav.customerSwitch.sessionNote') }}
        </p>
      </template>
    </Menu>

    <Dialog
      v-model:visible="showConfirm"
      modal
      :header="t('nav.customerSwitch.confirmTitle')"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p class="text-paragraph-text">
        {{ t('nav.customerSwitch.confirmBody', { name: pending?.name ?? '' }) }}
      </p>
      <template #footer>
        <Button
          :label="t('nav.customerSwitch.cancel')"
          severity="secondary"
          outlined
          @click="cancelSwitch"
        />
        <Button
          :label="t('nav.customerSwitch.confirm')"
          :loading="switching"
          data-cy="customer-switch-confirm"
          @click="performSwitch"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import type { Customer } from '@/services/authServices'
import { hasUnsavedChanges } from '@/composables/useUnsavedChanges'
import { markCustomerSwitched } from '@/utils/customerSwitch'

const { t } = useI18n()
const authStore = useAuthStore()

const menu = ref<InstanceType<typeof Menu> | null>(null)
const menuOpen = ref(false)
const switching = ref(false)
const showConfirm = ref(false)
/** The customer awaiting confirmation, kept across the dialog round-trip. */
const pending = ref<Customer | null>(null)

const activeName = computed(() => authStore.activeCustomer?.name ?? '')
const activeNumber = computed(() => authStore.activeCustomer?.number ?? null)

const customerItems = computed<MenuItem[]>(() =>
  authStore.assignedCustomers.map((customer) => ({
    label: customer.name,
    number: customer.number,
    isActive: customer.id === authStore.activeCustomer?.id,
    command: () => selectCustomer(customer),
  })),
)

function toggleMenu(event: Event) {
  menu.value?.toggle(event)
}

function selectCustomer(customer: Customer) {
  // Re-picking the active company is a no-op, not a reload.
  if (customer.id === authStore.activeCustomer?.id) return

  pending.value = customer

  if (hasUnsavedChanges()) {
    showConfirm.value = true
    return
  }
  performSwitch()
}

function cancelSwitch() {
  showConfirm.value = false
  pending.value = null
}

async function performSwitch() {
  const customer = pending.value
  if (!customer || switching.value) return

  switching.value = true
  try {
    await authStore.switchCustomer(customer.id)

    // Park the confirmation — the reload below discards in-flight toasts.
    markCustomerSwitched(customer.name)

    // A full reload is the only reliable way to guarantee that no store, cached
    // list or open detail page still holds the previous company's data, and it
    // lands the user on the start page rather than on a record they can no
    // longer see.
    window.location.assign(import.meta.env.BASE_URL || '/')
  } catch {
    // httpClient already surfaced the error toast; stay where we are so nothing
    // is lost, and let the user try again.
    switching.value = false
    showConfirm.value = false
    pending.value = null
  }
}
</script>
